<template>
 <section>
      <div class="container cont checkout-step checkout-step--clean">
        <div class="checkout-step__intro">
          <small class="form-text text-muted checkout-step__eyebrow">Paso 1 de 4</small>
          <h2 class="va-h6 checkout-step__title"><strong>Selecciona tus accesos</strong></h2>
          <p class="checkout-step__hint">Elige la cantidad ideal y confirma tu total en una sola vista.</p>
          <div v-if="availableTickets.length > 1 && total_quantity === 0" class="checkout-step__scroll-cue" aria-hidden="true">
            <span>Desliza para ver mas opciones</span>
            <i class="fas fa-arrow-down"></i>
          </div>
        </div>

        <div class="checkout-step__selection-layout">
          <div class="checkout-step__selection-main">
            <div class="checkout-ticket-grid">
              <CheckoutTicketCard
                v-for="(eventTicket, index) in availableTickets"
                :key="eventTicket.id"
                :ticket="eventTicket"
                :quantity="eventTicket.quantity"
                :max-quantity="Math.min(Number(eventTicket.max_quantity) || 10, ticketStock(eventTicket))"
                :unit-amount="eventTicket.amount"
                :currency="presentmentCurrency"
                :tax-text="eventTicket.tax_amount > 0 ? taxLine(eventTicket) : ''"
                :availability="dateValidation(eventTicket.expiration_date, eventTicket.sales_date, eventTicket)"
                :low-availability="shouldShowLowAvailability(eventTicket) ? lowAvailabilityCount(eventTicket) : 0"
                :style="{ animationDelay: `${index * 70}ms` }"
                @increment="increment(eventTicket.quantity, index)"
                @decrement="decrement(eventTicket.quantity, index)"
              />
            </div>
          </div>

          <aside :class="['checkout-step__selection-summary', { 'is-empty': total_quantity === 0 }]" aria-live="polite">
            <span class="checkout-step__selection-kicker">{{ summaryKicker }}</span>

            <div class="checkout-step__selection-head">
              <div class="checkout-step__selection-copy">
                <strong>{{ summaryHeadline }}</strong>
                <p>{{ summaryCopy }}</p>
              </div>
              <span class="checkout-step__selection-total checkout-step__selection-total--inline">{{ formattedSubtotal }}</span>
            </div>

            <div class="checkout-step__selection-meta">
              <span>{{ total_quantity || 0 }} acceso{{ total_quantity === 1 ? '' : 's' }}</span>
              <span>{{ selectedTypeCount || 0 }} tipo{{ selectedTypeCount === 1 ? '' : 's' }}</span>
              <span>{{ summaryMeta }}</span>
            </div>

            <div v-if="selectedTickets.length" class="checkout-step__selection-list">
              <article v-for="ticket in selectedTickets" :key="ticket.id" class="checkout-step__selection-item">
                <div>
                  <strong>{{ ticket.name }}</strong>
                  <small>x{{ ticket.quantity }}</small>
                </div>
                <span>{{ formatMoney(ticket.total) }}</span>
              </article>
            </div>

            <div v-if="taxes > 0" class="checkout-step__selection-item checkout-step__selection-item--subtotal-base">
              <div>
                <strong>Subtotal</strong>
                <small>Antes de impuestos</small>
              </div>
              <span>{{ formattedSubtotalBase }}</span>
            </div>

            <div v-if="taxes > 0" class="checkout-step__selection-item checkout-step__selection-item--tax">
              <div>
                <strong>{{ selectedTaxLabel }}</strong>
                <small>Se suma al subtotal</small>
              </div>
              <span>{{ formatMoney(taxes) }}</span>
            </div>

            <div v-else class="checkout-step__selection-empty">
              Tu resumen aparecerá aquí con accesos, cantidades y subtotal apenas agregues la primera selección.
            </div>

            <div class="checkout-step__selection-footer">
              <span class="checkout-step__selection-total checkout-step__selection-total--footer">{{ formattedSubtotal }}</span>

              <button @click="continueSale()" :disabled="total_quantity===0" class="btn btn-primary checkout-step__button checkout-step__button--summary liquid-page-button" type="button">
                <span class="liquid-page-button__glow"></span>
                <span class="liquid-page-button__ring"></span>
                <span class="liquid-page-button__inner">
                  <span class="liquid-page-button__copy">{{ summaryCta }}</span>
                  <span class="liquid-page-button__icon"><i class="fas fa-arrow-right"></i></span>
                </span>
              </button>
            </div>
          </aside>
        </div>

        <Teleport to="body">
          <div class="checkout-step__mobile-dock" aria-live="polite">
            <div :class="['checkout-step__mobile-card', { 'is-empty': total_quantity === 0 }]">
              <div class="checkout-step__mobile-copy">
                <span class="checkout-step__mobile-total">{{ formattedSubtotal }}</span>
                <strong>{{ mobileHeadline }}</strong>
                <small>{{ mobileSummaryLine }}</small>
              </div>

              <button @click="continueSale()" :disabled="total_quantity===0" class="btn btn-primary checkout-step__button checkout-step__button--mobile liquid-page-button" type="button">
                <span class="liquid-page-button__glow"></span>
                <span class="liquid-page-button__ring"></span>
                <span class="liquid-page-button__inner">
                  <span class="liquid-page-button__copy">{{ mobileCta }}</span>
                  <span class="liquid-page-button__icon"><i class="fas fa-arrow-right"></i></span>
                </span>
              </button>
            </div>
          </div>
        </Teleport>

      </div>
  </section>
</template>


<script>
import CheckoutTicketCard from './CheckoutTicketCard.vue'
import { ticketAvailability, ticketStock } from '../../common/ticketAvailability.js'
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../common/currency.js'
import { normalizeTicketTaxPercent, ticketUnitPricing } from '../../common/ticketTaxes.js'

export default {
  
    name: 'SaleQuantity',
    components: { CheckoutTicketCard },
    
    props:{
        eventTickets: {
          type: Array,
          default: () => [],
       },
       coordinated_fee:{
         required: true
       },

       fixed_fee:{
         required: true
        },

        paymentOptions: {
          type: Array,
          default: () => [],
        },

        presentmentCurrency: {
          type: String,
          default: 'USD',
        },


       coordinated_service:{
         required: true
        }
     },

     

     data(){
     return{
       events:[],
       next:null,
       availableTickets:[],
       order:[],
        total: 0.00,
        subtotal: 0.00,
        taxes: 0.00,
        fee: 0.00,
       total_quantity: 0,
       today: new Date(),
       hasMap: 0
       
     }
     

    },

    computed: {
      selectedTypeCount() {
        return this.availableTickets.filter((ticket) => ticket.quantity > 0).length
      },

      selectedTickets() {
        return this.availableTickets
          .filter((ticket) => ticket.quantity > 0)
          .map((ticket) => ({
            id: ticket.id,
            name: ticket.name,
            quantity: ticket.quantity,
            total: (Number.parseFloat(ticket.total) || 0).toFixed(2),
          }))
      },

      formattedSubtotal() {
        const subtotal = Number.parseFloat(this.subtotal) || 0
        return this.formatMoney(subtotal)
      },

      subtotalBase() {
        const subtotal = Number.parseFloat(this.subtotal) || 0
        const taxes = Number.parseFloat(this.taxes) || 0
        return Math.max(subtotal - taxes, 0)
      },

      formattedSubtotalBase() {
        return this.formatMoney(this.subtotalBase)
      },

      selectedTaxLabel() {
        const labels = this.availableTickets
          .filter((ticket) => ticket.quantity > 0 && Number.parseFloat(ticket.tax_amount) > 0)
          .map((ticket) => ticket.tax_label || 'IVA')
        const uniqueLabels = [...new Set(labels)]
        return uniqueLabels.length === 1 ? uniqueLabels[0] : 'Impuestos'
      },

      summaryKicker() {
        return 'Resumen'
      },

      summaryHeadline() {
        if (this.total_quantity === 0) {
          return 'Sin accesos seleccionados'
        }

        return `${this.total_quantity} acceso${this.total_quantity === 1 ? '' : 's'} seleccionado${this.total_quantity === 1 ? '' : 's'}`
      },

      mobileHeadline() {
        if (this.total_quantity === 0) {
          return 'Elige tus accesos'
        }

        return `${this.total_quantity} acceso${this.total_quantity === 1 ? '' : 's'} listo${this.total_quantity === 1 ? '' : 's'}`
      },

      summaryCopy() {
        if (this.total_quantity === 0) {
          return 'Agrega al menos un acceso para ver el subtotal y habilitar el siguiente paso.'
        }

        return 'Revisa tu selección, el subtotal y los tipos elegidos antes de continuar a datos.'
      },

      summaryMeta() {
        if (this.total_quantity === 0) {
          return 'Siguiente paso bloqueado'
        }

        return 'Siguiente: datos del comprador'
      },

      mobileSummaryLine() {
        if (this.total_quantity === 0) {
          return 'Tu subtotal aparecerá aquí cuando agregues un acceso.'
        }

        if ((Number.parseFloat(this.taxes) || 0) > 0) {
          return `Subtotal ${this.formattedSubtotalBase} + ${this.selectedTaxLabel} ${this.formatMoney(this.taxes)}`
        }

        return this.selectedTickets
          .slice(0, 2)
          .map((ticket) => `${ticket.name} x${ticket.quantity}`)
          .join(' · ')
      },

      summaryCta() {
        return this.total_quantity === 0 ? 'Elige tu primer acceso' : 'Continuar con datos'
      },

      mobileCta() {
        return this.total_quantity === 0 ? 'Elegir acceso' : 'Continuar'
      },
    },

     created () {
     this.createTable()
    this.today = new Date();
  },

  
   methods:{
    ticketStock,
    normalizeCurrency(value) {
      return normalizeCurrencyCode(value || this.presentmentCurrency || 'USD', 'USD')
    },

    formatMoney(value) {
      return formatCurrencyAmount(value, this.normalizeCurrency(this.presentmentCurrency))
    },

    taxLine(ticket) {
      const percent = normalizeTicketTaxPercent(ticket.tax_percent) * 100
      const suffix = ticket.tax_price_includes_tax ? 'incluido' : 'incluye'
      return `${ticket.tax_label || 'IVA'} ${percent.toFixed(2).replace(/\.00$/, '')}% ${suffix}`
    },

    buttonAnimation(event) {
                event.target.style.transform = 'scale(0.9)';
            },
            resetAnimation(event) {
                event.target.style.transform = 'scale(1)';
            },
      lowAvailabilityCount(ticket) {
        const counts = [ticket.remaining_tickets, ticket.host_remaining_tickets]
          .map((value) => Number.parseInt(value, 10))
          .filter((value) => Number.isFinite(value) && value >= 0)

        if (!counts.length) {
          return 0
        }

        return Math.min(...counts)
      },

      shouldShowLowAvailability(ticket) {
        if (this.dateValidation(ticket.expiration_date, ticket.sales_date, ticket) !== 1) {
          return false
        }

        const remaining = this.lowAvailabilityCount(ticket)
        return remaining > 0 && remaining < 10
      },

      createTable() {


    for (var i = 0; i < this.eventTickets.length; i++){
      console.log(i)

      if (this.eventTickets[i].status==1){
      const pricing = ticketUnitPricing(this.eventTickets[i])
      let uProduct =  {sold_out: this.eventTickets[i].sold_out, is_sold_out: this.eventTickets[i].is_sold_out, remaining_tickets: this.eventTickets[i].remaining_tickets, host_remaining_tickets: this.eventTickets[i].host_remaining_tickets,   quantity:0, amount: pricing.grossAmount, base_amount: pricing.baseAmount, tax_amount: pricing.taxAmount, tax_label: pricing.taxLabel, tax_percent: this.eventTickets[i].tax_percent, tax_price_includes_tax: pricing.priceIncludesTax, tax_enabled: Boolean(this.eventTickets[i].tax_enabled),  name: this.eventTickets[i].name, is_courtesy:  this.eventTickets[i].is_courtesy, total: 0, id: this.eventTickets[i].id, sales_date: this.eventTickets[i].sales_date,  expiration_date: this.eventTickets[i].expiration_date, quantity_factor: this.eventTickets[i].quantity, map: this.eventTickets[i].has_map, max_quantity: this.eventTickets[i].max_quantity, description: this.eventTickets[i].description, generic_image: this.eventTickets[i].generic_image, guest_fields: Array.isArray(this.eventTickets[i].guest_fields) ? this.eventTickets[i].guest_fields : []};
      this.availableTickets=this.availableTickets.concat(uProduct)
      

    }
        }

      
          
      },

  activePricingProfile() {
    if (!Array.isArray(this.paymentOptions) || this.paymentOptions.length === 0) {
      return null
    }

    const option = this.paymentOptions[0]
    if (!option || !option.pricing_profile) {
      return null
    }

    return option.pricing_profile
  },

  continueSale(){
    if(this.subtotal!=0){
        //this.fee=(this.subtotal * 0.083333) + 0.5
        const pricingProfile = this.activePricingProfile()
        let coordinated_fee=parseFloat(pricingProfile?.platform_fee_percent ?? this.coordinated_fee)
        let coordinated_service=parseFloat(pricingProfile?.platform_fee_per_ticket ?? this.coordinated_service)
        let fixed_fee=parseFloat(pricingProfile?.platform_fee_fixed ?? this.fixed_fee)

        this.fee=(this.subtotal *coordinated_fee) + (coordinated_service * this.total_quantity) + fixed_fee
      
    }else{
      this.fee=0
    }
      this.total=this.subtotal+this.fee
      
      this.subtotal=this.subtotal.toFixed(2)
      this.taxes=this.taxes.toFixed(2)
      this.fee=this.fee.toFixed(2)
      this.total=this.total.toFixed(2)


      this.$emit('messageFromCart', this.availableTickets, this.subtotal, this.fee, this.total, this.hasMap, this.taxes)
    },


    dateValidation(str, sales, obj) {
      const expirations = [str, obj.venue_expiration_date].filter(Boolean).map(value => new Date(value).getTime())
      const starts = [sales, obj.venue_sales_date].filter(Boolean).map(value => new Date(value).getTime())
      return ticketAvailability({ ...obj,
        expiration_date: expirations.length ? Math.min(...expirations) : null,
        sales_date: starts.length ? Math.max(...starts) : null,
      })
    },

    openEvent(_id) {
      this.$router.push({ name: "event", params: { slug: _id } });
    },


    increment (quantity, i) {
      const ticket = this.availableTickets[i]
      if (this.dateValidation(ticket.expiration_date, ticket.sales_date, ticket) !== 1) return
      const max = Math.min(Number(ticket.max_quantity) || 10, ticketStock(ticket))
      if (ticket.quantity >= max) return
      ticket.quantity++
      ticket.amount = Number(ticket.amount)
      ticket.total = (ticket.quantity * ticket.amount).toFixed(2)
      this.subtotal += ticket.amount
      this.taxes += Number.parseFloat(ticket.tax_amount) || 0
      this.total_quantity += ticket.quantity_factor
      if (ticket.map) this.hasMap++
    },
    decrement (quantity, i) {
      if(quantity === 0) {
         this.availableTickets[i].quantity=0
           }else{

     
        this.availableTickets[i].quantity--
      this.availableTickets[i].total= (this.availableTickets[i].quantity * this.availableTickets[i].amount).toFixed(2)
       this.availableTickets[i].amount=this.availableTickets[i].amount*1
      this.subtotal=this.subtotal - this.availableTickets[i].amount
      this.taxes=this.taxes - (Number.parseFloat(this.availableTickets[i].tax_amount) || 0)
      this.subtotal.toFixed(2)
      this.total_quantity=this.total_quantity-(this.availableTickets[i].quantity_factor)

      if (this.availableTickets[i].map==true){
        this.hasMap--
      }

       }
    
      
    }

    
   }
}
</script>

<style>

#imagemPosterSlide{
  padding: 10px;
  
}

.container{
  justify-content:center
}

.checkout-ticket-card__price small {
  display: block;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.76rem;
  font-weight: 600;
}

.checkout-step__selection-item--tax {
  opacity: 0.86;
}


  
</style>



<style lang="css">
h1 {
  text-align: center;
  margin-bottom: 50px;
  margin-top: 50px;
}

.blog-card-blog {
  margin-top: 30px;
}

.blog-card {
  display: inline-block;
  position: relative;
  width: 100%;
  border-radius: 6px;
  color: rgba(0, 0, 0, 0.87);
  background: rgb(52, 58, 64);
  background-color: rgb(52, 58, 64);
}



.blog-card .blog-table {
  padding: 15px 30px;
}

.blog-table {
  margin-bottom: 0px;
}

.blog-category {
  position: relative;
  line-height: 0;
  margin: 15px 0;
}

.blog-text-success {
  color: rgb(198,197,197);
}

.blog-card-blog .blog-card-caption {
  margin-top: 5px;
}

.blog-card-caption {
  font-weight: 700;
  font-family: "Roboto Slab", "Times New Roman", serif;
}

.blog-card-caption, .blog-card-caption a {
  color: #333;
  text-decoration: none;
}

p {
  color: #3C4857;
  margin-top: 0;
  margin-bottom: 1rem;
}

.blog-card .ftr {
  margin-top: 15px;
}

.blog-card .ftr .author {
  color: #888;
}

.blog-card .ftr div {
  display: inline-block;
}

.blog-card .author .avatar {
  width: 36px;
  height: 36px;
  overflow: hidden;
  border-radius: 50%;
  margin-right: 5px;
}

.blog-card .ftr .stats {
  position: relative;
  top: 1px;
  font-size: 14px;
}

.blog-card .ftr .stats {
  float: right;
  line-height: 30px;
}

a {
  text-decoration: none;
}




  .quantity-toggle input {
    width: 2.5rem;
    text-align: center;
    padding: 0 .5rem;
    background: transparent;
  }
  
  .quantity-toggle button {
    background: transparent;
    color: white;
    font-size: 1rem;
    cursor: pointer;
    border: none;
    
  }


.cont{
   padding-right: 5px;
   padding-left: 5px;
}

.checkout-ticket-card__availability--low {
  background:
    radial-gradient(circle at 14% 0%, rgba(255, 255, 255, 0.14), transparent 30%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.03)),
    rgba(10, 11, 16, 0.82) !important;
  border: 1px solid rgba(214, 206, 236, 0.22) !important;
  color: #eef3ff !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    0 10px 24px rgba(0, 0, 0, 0.16),
    0 0 0 1px rgba(178, 165, 220, 0.06);
}

.checkout-ticket-card__availability--low::before {
  background: linear-gradient(180deg, #f5f6fb, #9d88de) !important;
  box-shadow: 0 0 12px rgba(157, 136, 222, 0.34) !important;
}


@media (max-width: 768px) {
.cont {
    padding-left: 1px;
    padding-right: 1px;
}

.rw{
   padding-right: 10px;
   padding-left: 10px;


}
}

    
</style>

   
